import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Icon from './Icon';
import SplitText from '../motion/SplitText';
import Counter from '../motion/Counter';
import Magnetic from '../motion/Magnetic';
import { Reveal, Stagger, StaggerItem } from '../motion/Reveal';
import { CircleButton } from './Footer';
import { STATS } from '../data/site';

/* Section heading — numbered kicker, masked headline, lead */
export function SectionHead({ num, kicker, title, lead, center = false, children }) {
  return (
    <div className={`section-head${center ? ' center' : ''}`}>
      {kicker && <Reveal from="none" className="kicker">{num && <b>{num}</b>}{kicker}</Reveal>}
      <SplitText as="h2" className="h2" text={title} inView stagger={0.05} />
      {lead && <Reveal delay={0.2}><p className="lead">{lead}</p></Reveal>}
      {children}
    </div>
  );
}

/* Arrow button */
export function ArrowButton({ to, href, children, variant = 'primary', small, magnetic = true, ...rest }) {
  const cls = `btn btn-${variant}${small ? ' btn-sm' : ''}`;
  const inner = <>{children}<span className="arrow"><Icon name="arrow" size={small ? 14 : 16} /></span></>;
  const el = href ? <a href={href} className={cls} {...rest}>{inner}</a> : <Link to={to} className={cls} {...rest}>{inner}</Link>;
  return magnetic ? <Magnetic strength={0.22}>{el}</Magnetic> : el;
}

/* Inner-page hero: fits one screen — masked headline, floating image stack with 3D entrance + scroll parallax */
export function PageHero({ title, lead, images = [], children }) {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 600], [0, -90]);
  const y2 = useTransform(scrollY, [0, 600], [0, 60]);
  const rot = useTransform(scrollY, [0, 600], [0, 6]);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
    ref.current.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
  };
  return (
    <section className="phero" ref={ref} onPointerMove={onMove}>
      <div className="hero-bg" /><div className="grid-bg" />
      <div className="container phero-grid">
        <div className="phero-copy">
          <SplitText as="h1" className="h1" text={title} delay={0.15} />
          <motion.p className="lead" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.7 }}>{lead}</motion.p>
          {children && <motion.div className="phero-actions" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.7 }}>{children}</motion.div>}
        </div>
        {images.length > 0 && (
          <div className="phero-media">
            <motion.div className="phero-img a" style={{ y: y1, rotate: rot }} initial={{ opacity: 0, scale: 0.85, rotateY: -25 }} animate={{ opacity: 1, scale: 1, rotateY: 0 }} transition={{ delay: 0.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
              <img src={images[0]} alt="" fetchpriority="high" decoding="async" />
            </motion.div>
            {images[1] && (
              <motion.div className="phero-img b" style={{ y: y2 }} initial={{ opacity: 0, x: 60, rotateY: 30 }} animate={{ opacity: 1, x: 0, rotateY: 0 }} transition={{ delay: 0.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
                <img src={images[1]} alt="" decoding="async" />
              </motion.div>
            )}
            <motion.div className="phero-badge glass float" initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.55, type: 'spring', stiffness: 200, damping: 14 }}>
              <span className="ico"><Icon name="award" size={20} /></span>
              <div><strong>10+ yrs</strong><span>Civil PMC excellence</span></div>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
}

/* Big animated counters */
export function StatsRow({ items = STATS, dark = false }) {
  return (
    <Stagger className={`stats-row${dark ? ' dark' : ''}`} gap={0.1}>
      {items.map((s) => (
        <StaggerItem key={s.label} className="stat-cell">
          <div className="stat-num"><Counter to={s.end} suffix={s.suffix} /></div>
          <div className="stat-label">{s.label}</div>
          {s.sub && <div className="stat-sub">{s.sub}</div>}
        </StaggerItem>
      ))}
    </Stagger>
  );
}

/* Dark closing call-to-action */
export function CTABand({ title = "Ready to build|with *confidence?*", text = 'Whether you need an independent PMC or a contractor who builds to PMC standards, we deliver exceptional results in quality, timeline and cost.' }) {
  return (
    <section className="section-sm">
      <div className="container">
        <Reveal from="zoom" className="cta-card">
          <div className="aurora" /><div className="grid-bg" />
          <div className="cta-copy">
            <SplitText as="h2" className="h2" text={title} inView />
            <p>{text}</p>
            <div className="cta-actions">
              <ArrowButton to="/request-consultation" variant="white">Request Consultation</ArrowButton>
              <ArrowButton to="/services" variant="ghost-light">Explore Services</ArrowButton>
            </div>
          </div>
          <div className="cta-circle"><CircleButton /></div>
        </Reveal>
      </div>
    </section>
  );
}
